import {
  CHART_ARTIFACT_TYPE,
  CHART_DASHBOARD_ARTIFACT_TYPE,
} from '@cotera/api';
import { Icon, type IconName } from '@cotera/client/app/components/ui';

/**
 * Maps an artifact's `type` discriminator to a name in the shared {@link Icon}
 * registry, so artifact surfaces render through the same icon system as the
 * rest of the app (e.g. the workflows list). Covers the canonical resource
 * kinds plus the artifact-only ones (`chart`, `chart-dashboard`, `folder`,
 * `workflow-node`). Unknown types fall back to the sparkles icon.
 */
const ARTIFACT_TYPE_ICON: Record<string, IconName> = {
  agent: 'agent',
  document: 'document',
  workflow: 'workflow',
  'workflow-node': 'workflow',
  dataset: 'dataset',
  folder: 'folder',
  [CHART_ARTIFACT_TYPE]: 'bar-chart',
  [CHART_DASHBOARD_ARTIFACT_TYPE]: 'presentation-chart-bar',
};

/** The {@link Icon} registry name for an artifact `type`, defaulting to sparkles. */
export const artifactTypeIcon = (artifactType: string): IconName =>
  ARTIFACT_TYPE_ICON[artifactType] ?? 'sparkles';

/**
 * Renders the resource-type icon for an artifact `type` via the shared
 * {@link Icon} component. Size is controlled with the `size` prop (not `h-*`/
 * `w-*` in `className`, which would collide with the icon's own size classes).
 */
export const ArtifactIcon: React.FC<{
  artifactType: string;
  className?: string;
  size?: 'small' | 'regular' | 'large';
}> = ({ artifactType, className, size }) => (
  <Icon
    icon={artifactTypeIcon(artifactType)}
    size={size}
    className={className}
  />
);
